import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { publicEndpoints } from "@/lib/api";
import { toast } from "sonner";
import { PublicHeader, PublicFooter, ChapterBadge } from "@/components/PublicChrome";
import {
  ArrowLeft,
  Check,
  MapPin,
  Users,
  Clock,
  Ticket,
  KeyRound,
} from "lucide-react";

export default function Reserver() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [parcoursList, setParcoursList] = useState([]);
  const [selected, setSelected] = useState(searchParams.get("parcours") || "");
  const [parcours, setParcours] = useState(null);
  const [form, setForm] = useState({
    prenom: "",
    email: "",
    telephone: "",
    nbJoueurs: 2,
    nbEquipes: 1,
    creneau: "",
    dateJeu: "",
  });
  const [step, setStep] = useState("parcours");
  const [loading, setLoading] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    const sid = searchParams.get("session_id");
    if (sid) {
      setStep("verifying");
      pollStatus(sid, 0);
    } else if (searchParams.get("cancelled")) {
      toast.error("Paiement annulé — votre réservation reste en attente.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function pollStatus(sid, attempt) {
    if (attempt >= 10) {
      setStep("payment-error");
      return;
    }
    try {
      const res = await publicEndpoints.paymentStatus(sid);
      if (res.paymentStatus === "paid" && res.reservation) {
        setConfirmation({
          reservation: res.reservation,
          montantTTC: res.reservation.montantTTC,
          message: "Paiement confirmé — votre passage est scellé.",
        });
        setStep("confirmed");
        toast.success("Paiement confirmé !");
        return;
      }
      if (res.status === "expired") {
        setStep("payment-error");
        return;
      }
      setTimeout(() => pollStatus(sid, attempt + 1), 2000);
    } catch {
      setTimeout(() => pollStatus(sid, attempt + 1), 2000);
    }
  }

  useEffect(() => {
    publicEndpoints.parcours().then(setParcoursList).catch(console.error);
  }, []);

  useEffect(() => {
    if (selected) {
      publicEndpoints
        .parcoursDetail(selected)
        .then((p) => {
          setParcours(p);
          if (p.creneaux?.length)
            setForm((f) => ({ ...f, creneau: p.creneaux[0] }));
        })
        .catch(console.error);
    }
  }, [selected]);

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const dateJeu = new Date(form.dateJeu).toISOString();
      const res = await publicEndpoints.reserver({
        parcoursId: selected,
        email: form.email,
        prenom: form.prenom,
        telephone: form.telephone || null,
        nbJoueurs: form.nbJoueurs,
        nbEquipes: form.nbEquipes,
        creneau: form.creneau || null,
        dateJeu,
      });
      const checkout = await publicEndpoints.checkout({
        reservationId: res.reservation.id,
        email: form.email,
        originUrl: window.location.origin,
      });
      window.location.href = checkout.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
      setLoading(false);
    }
  }

  /* --- Vérification du paiement (retour Stripe) --- */
  if (step === "verifying" || step === "payment-error") {
    return (
      <div
        className="min-h-screen bg-noir text-parchemin bg-grain flex flex-col"
        data-testid="payment-verification"
      >
        <PublicHeader current="reserver" />
        <main className="flex-1 flex items-center justify-center px-6 py-16">
          <div className="max-w-lg w-full text-center border border-or/30 bg-noir-50/60 backdrop-blur-sm rounded-sm p-10">
            {step === "verifying" ? (
              <>
                <div className="mx-auto w-16 h-16 rounded-full border-2 border-or/50 border-t-or flex items-center justify-center mb-6 animate-spin" />
                <h1
                  className="font-display font-medium text-2xl md:text-3xl text-parchemin"
                  data-testid="payment-verifying-title"
                >
                  Vérification du <span className="italic text-or">paiement…</span>
                </h1>
                <p className="mt-4 text-sm text-parchemin/70">
                  Un instant, nous scellons votre réservation.
                </p>
              </>
            ) : (
              <>
                <h1
                  className="font-display font-medium text-2xl md:text-3xl text-parchemin"
                  data-testid="payment-error-title"
                >
                  Paiement non <span className="italic text-or">confirmé</span>
                </h1>
                <p className="mt-4 text-sm text-parchemin/70">
                  Nous n&apos;avons pas pu confirmer votre paiement. Si vous avez
                  été débité, vérifiez vos emails ou contactez-nous.
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/reserver")}
                  data-testid="payment-retry-btn"
                  className="mt-8 inline-flex items-center gap-2 px-6 py-3 border border-or/30 hover:border-or hover:bg-or/5 text-parchemin rounded-sm text-sm tracking-wide transition-all duration-300"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Réessayer
                </button>
              </>
            )}
          </div>
        </main>
        <PublicFooter />
      </div>
    );
  }

  /* --- Écran de confirmation --- */
  if (step === "confirmed" && confirmation) {
    return (
      <div
        className="min-h-screen bg-noir text-parchemin bg-grain flex flex-col"
        data-testid="reservation-confirmed"
      >
        <PublicHeader current="reserver" />

        <main className="flex-1 flex items-center justify-center px-6 py-16 relative overflow-hidden">
          <div className="max-w-lg w-full text-center relative">
            <div className="border border-or/30 bg-noir-50/60 backdrop-blur-sm rounded-sm p-10 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.9)]">
              <div className="mx-auto w-16 h-16 rounded-full border-2 border-or/50 flex items-center justify-center mb-6 animate-glow-pulse">
                <Check className="w-8 h-8 text-or" strokeWidth={2.5} />
              </div>

              <ChapterBadge testId="confirmed-chapter">
                Chapitre II · Le sceau
              </ChapterBadge>

              <h1 className="mt-5 font-display font-medium text-3xl md:text-4xl text-parchemin leading-tight">
                Le seuil vous est
                <br />
                <span className="italic text-or">réservé.</span>
              </h1>
              <p className="mt-4 text-sm text-parchemin/70">
                {confirmation.message}
              </p>

              <div className="mt-8 p-5 border border-or/25 bg-vert-900/40 rounded-sm space-y-3 text-left">
                <div className="flex items-center justify-between gap-4">
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-or/70">
                    Code d&apos;accès
                  </span>
                  <span
                    className="font-mono font-medium text-xl text-or stat-graved tracking-widest"
                    data-testid="confirmation-code"
                  >
                    {confirmation.reservation.codeAcces}
                  </span>
                </div>
                <div className="h-px bg-or/15" />
                <div className="flex items-center justify-between gap-4">
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-or/70">
                    Montant réglé
                  </span>
                  <span className="font-display text-xl text-parchemin">
                    {confirmation.montantTTC}€
                  </span>
                </div>
              </div>

              <Link
                to="/"
                data-testid="back-home"
                className="mt-8 group inline-flex items-center gap-2 px-6 py-3 border border-or/30 hover:border-or hover:bg-or/5 text-parchemin rounded-sm text-sm tracking-wide transition-all duration-300"
              >
                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                Retour à l&apos;accueil
              </Link>
            </div>
          </div>
        </main>

        <PublicFooter />
      </div>
    );
  }

  /* --- Tunnel de réservation --- */
  return (
    <div
      className="min-h-screen bg-noir text-parchemin bg-grain flex flex-col"
      data-testid="reserver-page"
    >
      <PublicHeader current="reserver" />

      <main className="flex-1 px-6 py-12 md:py-16">
        <div className="max-w-3xl mx-auto space-y-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-parchemin/50 hover:text-or text-sm transition-colors"
            data-testid="back-link"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Accueil
          </Link>

          <div>
            <ChapterBadge testId="reserver-chapter">
              Chapitre II · Le passage
            </ChapterBadge>
            <h1 className="mt-5 font-display font-medium text-4xl md:text-5xl text-parchemin leading-[1.05] tracking-tight">
              Réservez votre
              <br />
              <span className="italic text-or">passage</span>
            </h1>
            <p className="mt-4 text-parchemin/60 text-base max-w-xl">
              Deux étapes pour franchir le seuil. Choisissez votre lieu,
              indiquez vos coordonnées, tout se joue ensuite dehors.
            </p>
          </div>

          {/* Step 1 : parcours */}
          <section
            className="border border-or/20 bg-noir-50/50 rounded-sm p-6 md:p-8"
            data-testid="step-parcours"
          >
            <div className="flex items-center gap-3 mb-6">
              <StepNumber n={1} />
              <h2 className="font-display text-xl md:text-2xl text-parchemin">
                Choisissez votre parcours
              </h2>
            </div>

            {parcoursList.length === 0 ? (
              <div className="text-parchemin/50 text-sm py-8 text-center italic">
                Aucun parcours disponible pour le moment — revenez bientôt.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {parcoursList.map((p) => {
                  const isPicnic = p.type === "PICNIC_GAME";
                  const isSelected = selected === p.id;
                  return (
                    <button
                      key={p.id}
                      onClick={() => setSelected(p.id)}
                      data-testid={`select-parcours-${p.id}`}
                      type="button"
                      className={`group text-left p-5 rounded-sm border transition-all duration-300 ${
                        isSelected
                          ? isPicnic
                            ? "border-vert-50 bg-vert-900/40"
                            : "border-or bg-or/10"
                          : "border-or/15 bg-noir-100/40 hover:border-or/40"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="text-2xl leading-none">
                          {isPicnic ? "🎒" : "🔍"}
                        </div>
                        <span
                          className={`font-mono text-[10px] tracking-[0.2em] uppercase ${
                            isSelected ? "text-or" : "text-parchemin/40"
                          }`}
                        >
                          {isPicnic ? "Porte I" : "Porte II"}
                        </span>
                      </div>
                      <div className="mt-4 font-display text-xl text-parchemin">
                        {p.nom}
                      </div>
                      <div className="mt-1 text-xs text-parchemin/50 flex items-center gap-1.5">
                        <MapPin className="w-3 h-3" />
                        {p.ville}
                      </div>
                      <div className="mt-4 pt-3 border-t border-or/10 flex items-center justify-between">
                        <span className="font-mono text-[11px] tracking-wider text-parchemin/60">
                          {p.dureeMin}min · max {p.maxJoueurs}
                        </span>
                        <span className="font-display text-lg text-or">
                          {p.prixPersonne}€
                          <span className="text-[10px] font-mono text-or/60 ml-1">
                            /pers
                          </span>
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          {/* Step 2 : form */}
          {selected && parcours && (
            <form
              onSubmit={submit}
              className="border border-or/20 bg-noir-50/50 rounded-sm p-6 md:p-8 space-y-6"
              data-testid="form-reservation"
            >
              <div className="flex items-center gap-3">
                <StepNumber n={2} />
                <h2 className="font-display text-xl md:text-2xl text-parchemin">
                  Vos coordonnées
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <In
                  label="Prénom"
                  value={form.prenom}
                  onChange={(v) => setForm({ ...form, prenom: v })}
                  required
                  testid="form-prenom"
                />
                <In
                  label="Email"
                  type="email"
                  value={form.email}
                  onChange={(v) => setForm({ ...form, email: v })}
                  required
                  testid="form-email"
                />
                <In
                  label="Téléphone"
                  value={form.telephone}
                  onChange={(v) => setForm({ ...form, telephone: v })}
                  testid="form-tel"
                />
                <In
                  label="Date de jeu"
                  type="date"
                  value={form.dateJeu}
                  onChange={(v) => setForm({ ...form, dateJeu: v })}
                  required
                  testid="form-date"
                />
                <In
                  label="Nb joueurs"
                  type="number"
                  min="1"
                  max="12"
                  value={form.nbJoueurs}
                  onChange={(v) =>
                    setForm({ ...form, nbJoueurs: parseInt(v, 10) })
                  }
                  required
                  testid="form-nbjoueurs"
                  icon={Users}
                />
                {parcours.type === "PICNIC_GAME" &&
                  (parcours.creneaux || []).length > 0 && (
                    <div className="flex flex-col gap-2">
                      <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-or/70 flex items-center gap-1.5">
                        <Clock className="w-3 h-3" />
                        Créneau
                      </label>
                      <select
                        data-testid="form-creneau"
                        value={form.creneau}
                        onChange={(e) =>
                          setForm({ ...form, creneau: e.target.value })
                        }
                        required
                        className="px-3.5 py-3 rounded-sm bg-noir-100 border border-or/20 text-parchemin text-sm outline-none focus:border-or focus:ring-1 focus:ring-or/30 transition"
                      >
                        {parcours.creneaux.map((c) => (
                          <option key={c} value={c} className="bg-noir">
                            {c}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
              </div>

              {/* Total */}
              <div className="p-5 border border-or/25 bg-vert-900/30 rounded-sm flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Ticket className="w-5 h-5 text-or" strokeWidth={1.5} />
                  <div>
                    <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-or/70">
                      Total
                    </div>
                    <div className="text-xs text-parchemin/60 mt-0.5">
                      {parcours.prixPersonne}€ × {form.nbJoueurs} joueurs
                    </div>
                  </div>
                </div>
                <div
                  className="font-display text-3xl text-or stat-graved"
                  data-testid="form-total"
                >
                  {(parcours.prixPersonne * form.nbJoueurs).toFixed(2)}€
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="btn-confirm-resa"
                className={`w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-sm font-medium text-sm tracking-wide transition-all duration-300 ${
                  loading
                    ? "bg-or/40 text-noir/60 cursor-not-allowed"
                    : "bg-or hover:bg-or-100 text-noir shadow-[0_10px_30px_-10px_rgba(200,146,42,0.5)]"
                }`}
              >
                {loading ? (
                  "Redirection vers le paiement…"
                ) : (
                  <>
                    <KeyRound className="w-4 h-4" />
                    Payer et sceller ma réservation
                  </>
                )}
              </button>

              <p className="text-xs text-parchemin/40 text-center italic font-display">
                Paiement sécurisé par Stripe · vous serez redirigé pour régler
                par carte bancaire.
              </p>
            </form>
          )}
        </div>
      </main>

      <PublicFooter />
    </div>
  );
}

/* --- Composants internes --- */

function StepNumber({ n }) {
  return (
    <div className="w-9 h-9 rounded-full border border-or/40 bg-noir flex items-center justify-center font-display text-lg text-or">
      {n}
    </div>
  );
}

function In({
  label,
  value,
  onChange,
  type = "text",
  required,
  min,
  max,
  testid,
  icon: Icon,
}) {
  return (
    <div className="flex flex-col gap-2">
      <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-or/70 flex items-center gap-1.5">
        {Icon && <Icon className="w-3 h-3" />}
        {label}
      </label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        required={required}
        min={min}
        max={max}
        onChange={(e) => onChange(e.target.value)}
        className="px-3.5 py-3 rounded-sm bg-noir-100 border border-or/20 text-parchemin text-sm outline-none focus:border-or focus:ring-1 focus:ring-or/30 transition"
      />
    </div>
  );
}
